import { Swatch } from '@tvg/fuse/swatch';
import classNames from 'classnames';
import withPrefetch from 'client/hoc/withPrefetch';
import { stringToChars } from 'client/utils/utils';
import { PLPGeneralFacetValue } from 'common/types/plp-data';
import React, { useMemo } from 'react';
import Checkbox from './Checkbox';
import styles from './CheckboxGroup.module.css';
import { colourConfig } from './ColourFacet/colourConfig';
import { Count } from './Count/Count';
import Fieldset from './Fieldset';
import { SEOAnchor as BaseSEOAnchor } from './SEOAnchor/SEOAnchor';

const SEOAnchor = withPrefetch(BaseSEOAnchor);

export interface CheckboxGroupProps {
  groupDescription: string;
  items: PLPGeneralFacetValue[];
  /**
   * @default ''
   */
  className?: string;
  /**
   * @default undefined
   */
  dataTestId?: string;
  /**
   * @default false
   */
  error?: boolean;
  /**
   * @default false
   */
  isFullWidth?: boolean;
  isShowCheckbox?: boolean;
  isSwatch?: boolean;
  groupID?: string;
  /**
   * @default () => {}
   */
  onChange: (event: React.FormEvent<HTMLElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void;
}

function CheckboxGroup({
  className,
  dataTestId: groupTestId,
  error,
  groupDescription,
  items,
  onChange,
  onKeyDown,
  isFullWidth,
  isSwatch,
  isShowCheckbox = true,
  groupID,
}: CheckboxGroupProps) {
  const classNameFull = className || 'facet-checkbox';
  const processedGroupID = useMemo(() => (groupID ? stringToChars(groupID) : undefined), [groupID]);

  return isShowCheckbox ? (
    <Fieldset
      className={`fuse-checkbox-group ${classNameFull}`}
      dataTestId={groupTestId}
      onChangeCallback={onChange}
      legend={groupDescription}
    >
      {items.map(({ name, id, count, url, selected }) => (
        <SEOAnchor href={url} key={name} className="forSEO" tabIndex={0} onKeyDown={onKeyDown}>
          <Checkbox
            error={!!error}
            name={name}
            value={id}
            dataTestId={groupTestId}
            className={classNameFull}
            fullWidth={isFullWidth}
            onChange={onChange}
            aria-label={`${name}, item count: ${count}`}
            componentId={processedGroupID}
            checked={selected}
            tabIndex={-1}
            marginBottom
          >
            {isSwatch && (
              <>
                <Swatch
                  className={styles.checkboxGroup__swatch}
                  colour={colourConfig[name.toLowerCase()] || name}
                  data-testid="facet-swatch"
                  size="large"
                />
                <span className={styles.checkboxGroup__swatchLabel} data-testid="facet-checkbox__label--swatch">
                  {name.toLowerCase()}
                </span>
              </>
            )}
            {!isSwatch && <span data-testid="facet-checkbox__label">{name}</span>}
            <Count data-testid="facet-checkbox__count">{count}</Count>
          </Checkbox>
        </SEOAnchor>
      ))}
    </Fieldset>
  ) : (
    <div>
      {items.map(({ name, id, count, url }) => (
        <SEOAnchor
          href={url}
          key={name}
          className="forSEO"
          data-value={id}
          onKeyDown={onKeyDown}
          aria-label={`${name} item count: ${count}`}
        >
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <div
            onClick={onChange}
            data-value={id}
            data-label={name}
            data-testid={groupTestId}
            className={classNames(styles.checkboxGroup__category, classNameFull, 'facet-category')}
          >
            <span data-testid="facet-checkbox__label">{name}</span>
            <Count data-testid="facet-checkbox__count">{count}</Count>
          </div>
        </SEOAnchor>
      ))}
    </div>
  );
}

export default React.memo(CheckboxGroup);
